import { resolveHubAuctionImage } from "./b2bHubHomeMedia";
import {
  getBuyerHighestBidAmount,
  getBuyerStartingPrice,
  hasBuyerBids,
  isBuyerAuctionReserveMet,
} from "./auctionPricing";

const SPEC_SLOT_COUNT = 4;

export function formatAuctionMetric(value) {
  if (value == null || value === "" || value === "—") return "—";
  return String(value);
}

export function formatAuctionCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

export function getAuctionProductAttributes(auction) {
  if (!auction?.product_attributes) return {};
  if (typeof auction.product_attributes === "string") {
    try {
      return JSON.parse(auction.product_attributes);
    } catch {
      return {};
    }
  }
  return auction.product_attributes;
}

export function getAuctionConditionGrade(auction) {
  const attrs = getAuctionProductAttributes(auction);
  return (
    attrs.condition_grade ||
    attrs.construction_condition_grade ||
    attrs.packaging_condition_grade ||
    attrs.energy_condition_grade ||
    null
  );
}

export function getAuctionDetailHref(auction) {
  return `/auction/${auction.auction_id}`;
}

export function getAuctionImagePath(auction) {
  return Array.isArray(auction?.images) && auction.images.length ? auction.images[0] : null;
}

export function getAuctionShareImage(auction) {
  const imagePath = getAuctionImagePath(auction);
  return imagePath ? resolveHubAuctionImage(imagePath, auction.auction_id) : "";
}

export function getAuctionLocation(auction) {
  return [auction?.city, auction?.state, auction?.country].filter(Boolean).join(", ");
}

export function getAuctionCategoryTrail(auction) {
  return [auction?.category_name, auction?.sub_category_name].filter(Boolean);
}

export function getAuctionTimeRemaining(endDate) {
  const diff = new Date(endDate).getTime() - Date.now();
  if (diff <= 0) return "Ended";
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h left`;
  const mins = Math.floor((diff % 3600000) / 60000);
  return mins > 0 ? `${mins}m left` : "<1m left";
}

export function isAuctionEnded(auction) {
  return new Date(auction?.end_date) <= new Date();
}

export function isAuctionBidAccepted(auction) {
  return auction?.seller_decision === "accepted" && Boolean(auction?.winning_bid_id);
}

export function canShowAuctionBidNow(auction) {
  const reserveMet = isBuyerAuctionReserveMet(auction);
  const endDateReached = isAuctionEnded(auction);
  const isEndOnScheduledOnly = auction?.auction_end_condition === "End on Scheduled Date Only";
  const isEndWhenReserveMet = auction?.auction_end_condition === "End When Reserve Met";
  return (
    !isAuctionBidAccepted(auction) &&
    !(isEndWhenReserveMet && reserveMet) &&
    !(isEndOnScheduledOnly && endDateReached)
  );
}

export function getAuctionStartingPriceLabel(auction) {
  const price = getBuyerStartingPrice(auction);
  return price > 0 ? `Starting ${formatAuctionCurrency(price)}` : "—";
}

export function getAuctionCurrentBidLabel(auction) {
  return hasBuyerBids(auction)
    ? formatAuctionCurrency(getBuyerHighestBidAmount(auction))
    : "No bids";
}

export function buildAuctionSpecItems(auction) {
  const specs = [
    { label: "Current Bid", value: getAuctionCurrentBidLabel(auction) },
    {
      label: "Bidders",
      value: formatAuctionMetric(auction?.bidder_count ?? auction?.total_bids ?? 0),
    },
    {
      label: "Increment",
      value: formatAuctionMetric(formatAuctionCurrency(auction?.bid_increment || 0)),
    },
    { label: "Time Left", value: formatAuctionMetric(getAuctionTimeRemaining(auction?.end_date)) },
  ];

  const slots = specs.slice(0, SPEC_SLOT_COUNT).map((spec) => ({ ...spec, empty: false }));
  while (slots.length < SPEC_SLOT_COUNT) {
    slots.push({ label: "", value: "", empty: true });
  }
  return slots;
}

export function resolveAuctionRibbon(auction) {
  if (isAuctionBidAccepted(auction)) {
    return { label: "BID ACCEPTED", className: "marketplace-product-grid-card__ribbon--certified" };
  }
  if (isAuctionEnded(auction)) {
    return { label: "ENDED", className: "marketplace-product-grid-card__ribbon--seller" };
  }
  if (isBuyerAuctionReserveMet(auction)) {
    return { label: "RESERVE MET", className: "marketplace-product-grid-card__ribbon--certified" };
  }
  const diff = new Date(auction?.end_date).getTime() - Date.now();
  if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
    return { label: "ENDING SOON", className: "marketplace-product-grid-card__ribbon--seller" };
  }
  return { label: "LIVE", className: "marketplace-product-grid-card__ribbon--seller" };
}

export function resolveAuctionRowBadge(auction) {
  const ribbon = resolveAuctionRibbon(auction);
  if (!ribbon) return null;
  return {
    label: ribbon.label,
    className:
      ribbon.className === "marketplace-product-grid-card__ribbon--certified"
        ? "marketplace-product-row__badge--certified"
        : "marketplace-product-row__badge--seller",
  };
}

export function getAuctionStatusChips(auction) {
  const chips = [];
  const condition = getAuctionConditionGrade(auction);
  if (condition) chips.push(condition);
  if (auction?.auction_end_condition) chips.push(auction.auction_end_condition);
  if (isAuctionBidAccepted(auction)) chips.push("Bid Accepted");
  return chips;
}
